import { useState } from "react";
import { Info } from "lucide-react";
import { toast } from "sonner";
import { useDemo } from "@/store/DemoStore";
import { btn } from "@/components/common";
import { addDays, fmtMoney, paymentSchedule, todayISO, tripTotal } from "@/lib/trip";

const input = "h-10 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";

export const defaultPlan = (departure) => {
  const depositDue = addDays(todayISO(), 7);
  const finalDue = departure ? addDays(departure, -90) : "";
  return { depositType: "percent", deposit: 20, depositDue, finalDue: finalDue && finalDue < depositDue ? depositDue : finalDue };
};

const L = ({ label, children }) => <label className="block min-w-0"><span className="mb-1.5 block text-xs font-medium text-slate-500">{label}</span>{children}</label>;

export function PlanFields({ plan, onChange, testPrefix = "plan" }) {
  const set = (k, num) => (e) => onChange({ ...plan, [k]: num ? Math.max(0, +e.target.value) : e.target.value });
  return (
    <div className="grid grid-cols-2 gap-3">
      <L label="Deposit type">
        <select className={input} value={plan.depositType} onChange={set("depositType")} data-testid={`${testPrefix}-deposit-type`}>
          <option value="percent">Percent of total</option>
          <option value="amount">Fixed amount</option>
        </select>
      </L>
      <L label={plan.depositType === "percent" ? "Deposit (%)" : "Deposit (USD)"}>
        <input type="number" min="0" max={plan.depositType === "percent" ? 100 : undefined} className={input} value={plan.deposit} onChange={set("deposit", true)} data-testid={`${testPrefix}-deposit-value`} />
      </L>
      <L label="Deposit due"><input type="date" className={input} value={plan.depositDue} onChange={set("depositDue")} data-testid={`${testPrefix}-deposit-due`} /></L>
      <L label="Final payment due"><input type="date" className={input} value={plan.finalDue} onChange={set("finalDue")} data-testid={`${testPrefix}-final-due`} /></L>
    </div>
  );
}

export const planError = (p) => (p.finalDue < p.depositDue ? "Final payment must be due on or after the deposit." : p.depositType === "percent" && p.deposit > 100 ? "Deposit can't exceed 100%." : null);

export function PaymentPlanEditor({ trip, onDone }) {
  const { updateTrip } = useDemo();
  const [plan, setPlan] = useState(() => {
    if (trip.plan) return trip.plan;
    const s = paymentSchedule(trip);
    const paid = s.filter((p) => p.paid);
    if (paid.length) return { depositType: "amount", deposit: paid.reduce((a, p) => a + p.amount, 0), depositDue: paid[paid.length - 1].due, finalDue: s[s.length - 1].due };
    return { depositType: "amount", deposit: s[0].amount, depositDue: defaultPlan(trip.departure).depositDue, finalDue: s[s.length - 1].due };
  });
  const preview = paymentSchedule({ ...trip, plan, status: "QUOTE" });
  const save = () => {
    const err = planError(plan);
    if (err) return toast.error(err);
    const paidCount = trip.plan ? trip.paidCount : trip.paidCount >= 3 ? 2 : trip.paidCount > 0 ? 1 : 0;
    updateTrip(trip.id, { plan, paidCount });
    toast.success("Payment plan saved");
    onDone();
  };
  return (
    <div className="space-y-4" data-testid="payment-plan-editor">
      <PlanFields plan={plan} onChange={setPlan} />
      <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm" data-testid="payment-plan-preview">
        {preview.map((p) => <p key={p.id} className="flex justify-between py-0.5"><span className="text-slate-500">{p.label}</span><span className="font-semibold text-navy-900">{fmtMoney(p.amount)}</span></p>)}
        {tripTotal(trip) === 0 && <p className="mt-1 text-xs text-slate-400">Amounts update once the invoice is built.</p>}
      </div>
      <p className="flex items-start gap-2 text-xs text-slate-500"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />Deposit + final payment for now. Installment plans can be added later.</p>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className={btn.secondary} data-testid="payment-plan-cancel-btn">Cancel</button>
        <button type="button" onClick={save} className={btn.primary} data-testid="payment-plan-save-btn">Save Plan</button>
      </div>
    </div>
  );
}
